/**
 * 各画面で使用できる検索条件
 */
export type SearchParams = {
  mainField?: MainFieldType;
  fields?: string[];
  marking?: string;
  isFiltering?: boolean;
  containsAverage?: boolean;
  days?: DaysType[];
  dayType?: DayType;
  dateSuffixes?: number[];
  dates?: number[];
  makers?: string[];
  makerGroups?: string[];
  kiList?: string[];
  sisTypes?: string[];
  shuGroupIds?: string[];
  shuList?: string[];
  hallsForDonyuJokyo?: string[];
  isDonyuForDonyuJokyo?: boolean;
  areas?: string[];
  halls?: string[];
  startDate?: string;
  endDate?: string;
  startComparisonDate?: string;
  endComparisonDate?: string;
  excludeToday?: boolean;
  sort?: string;
  order?: OrderType;
  kadoFieldType?: KadoFieldType;
};

/**
 * SettingsOption内の検索条件項目一覧
 */

export type SearchCondition = {
  makers: Option[];
  makerGroups?: MakerGroup[];
  kiList: Option[];
  kiTagList?: KiTagListOptions[];
  shuGroupList: ShuGroupList[];
  shuList: string[];
  areas: Area[];
  halls: Hall[];
  sisTypes: SisType[];
  latestBaseDateForDailySisData: string;
  latestBaseDateForWeeklySisData: string;
  latestBaseDateForMonthlySisData: string;
};

export type SearchConditionWithFlag = Omit<SearchCondition, 'halls'> & {
  halls: HallWithFlag[];
};

export type KiTagListOptions = {
  tagId: number;
  tagName: string;
  tagColor: string;
  viewOrder: number;
  kiList: string[];
};

export type SisType = {
  shuCode: string;
  viewOrder: number;
} & Option;

export const KADO_FIELD_TYPE = {
  VALUE: 'value',
  RATIO: 'ratio',
} as const;
export type KadoFieldType =
  (typeof KADO_FIELD_TYPE)[keyof typeof KADO_FIELD_TYPE];

export const MAIN_FIELD_TYPE = {
  KI_TUSHO_MEI: 'kiTushoMei',
  SHU: 'shu',
  TYPE: 'type',
  KITAG: 'kiTag',
  MAKER: 'maker',
  MAKER_GROUP: 'makerGroup',
  HL_MEI: 'hlMei',
  DAI_BAN: 'daiBan',
} as const;
export type MainFieldType =
  (typeof MAIN_FIELD_TYPE)[keyof typeof MAIN_FIELD_TYPE];

const MAIN_FIELD_TYPE_VALUES = new Set<string>(Object.values(MAIN_FIELD_TYPE));

export const isMainFieldType = (value: unknown): value is MainFieldType =>
  typeof value === 'string' && MAIN_FIELD_TYPE_VALUES.has(value);

export const DAYS_TYPE = {
  MONDAY: '月',
  TUESDAY: '火',
  WEDNESDAY: '水',
  THURSDAY: '木',
  FRIDAY: '金',
  SATURDAY: '土',
  SUNDAY: '日',
} as const;
export type DaysType = (typeof DAYS_TYPE)[keyof typeof DAYS_TYPE];

const DAYS_TYPE_VALUES = new Set<string>(Object.values(DAYS_TYPE));

export const isDaysType = (value: unknown): value is DaysType =>
  typeof value === 'string' && DAYS_TYPE_VALUES.has(value);

export const DAY_TYPE = {
  ALL: 'all',
  WEEKDAY: 'weekday',
  HOLIDAY: 'holiday',
} as const;
export type DayType = (typeof DAY_TYPE)[keyof typeof DAY_TYPE];

const DAY_TYPE_VALUES = new Set<string>(Object.values(DAY_TYPE));

export const isDayType = (value: unknown): value is DayType =>
  typeof value === 'string' && DAY_TYPE_VALUES.has(value);

export type OrderType = 'asc' | 'desc';

export const isOrderType = (value: unknown): value is OrderType =>
  value === 'asc' || value === 'desc';

export const SIS_DATE_RANGE_TYPE = {
  MONTHLY: 'monthly',
  WEEKLY: 'weekly',
} as const;
export type SisDateRangeType =
  (typeof SIS_DATE_RANGE_TYPE)[keyof typeof SIS_DATE_RANGE_TYPE];

export const SIS_FIELD_TYPE = {
  RESULT: 'result',
  DIFF: 'diff',
  RATIO: 'ratio',
} as const;
export type SisFieldType = (typeof SIS_FIELD_TYPE)[keyof typeof SIS_FIELD_TYPE];

export type SisBreakEvenForPachinko =
  | '01'
  | '02'
  | '03'
  | '04'
  | '05'
  | '06'
  | '07';
export type SisBreakEvenForPachislo = '01' | '02' | '03' | '04';
export type SisRank = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
export type SisArea =
  | '01'
  | '02'
  | '03'
  | '04'
  | '05'
  | '06'
  | '07'
  | '08'
  | '09'
  | '10'
  | '11';

export type ColumnForDataTable = {
  name: string;
  isSisField?: boolean | null;
  code: string | null;
  type?: 'number' | 'numberWithPrefix' | 'string' | 'numberOrString' | null;
  backgroundColor?: string | null;
  color?: string | null;
  description?: string | null;
};

export type DataElementForTable = {
  value: string;
  color: string;
  backgroundColor?: string | null;
};

// queryParameterが固定形式の場合の型
export type SortRows = {
  queryParameter: {
    kiList?: string[] | null;
    sisTypes?: string[] | null;
    makers?: string[] | null;
    halls?: string[] | null;
  };
  data: DataElementForTable[];
};

// queryParameterがキー(name, value)の場合の型
export type SortRowsForKey = RowsForKey;

export type RowsForKey = {
  queryParameter: {
    name: string;
    value: string | null;
  };
  data: DataElementForTable[];
};

/**
 * @deprecated ColumnForDataTableを使用してください
 */
type DeprecatedColumnForDataTable = {
  name: string;
  code: string;
  type: string;
  backgroundColor: string | null;
  color: string | null;
  isSisField: boolean;
  //TODO:全ての画面でdescriptionが来ているか確認できたらオプショナルを外す
  description?: string | null;
};

/**
 * @deprecated ColumnForDataTableを使用してください
 */
export type Column = DeprecatedColumnForDataTable & {
  columns?: DeprecatedColumnForDataTable[];
};

export type NestedColumn = ColumnForDataTable & {
  columns?: ColumnForDataTable[];
};

export type Row<T = QueryParameter> = {
  queryParameter: T;
  data: Item[];
};

/**
 * @deprecated DataElementForTableを使用してください
 */
export type Item = {
  value: string; // TODO: nullableなので修正が必要
  color: string;
  backgroundColor: string | null;
};

export type QueryParameter = {
  name: string;
  value: string;
};

// FieldSettings
export type Option = {
  code: string;
  name: string;
  columnGroup?: string[];
  genres?: string[];
  isDefault?: boolean;
  isEnabled?: boolean;
  viewOrder?: number;
  unsupportedMainFields?: MainFieldType[];
  isSelectableForGraph?: boolean;
  supportsSis?: boolean;
  description?: string;
  isNew?: boolean;
  shuType?: string;
  subColumnDisplayName?: string;
  groupCd?: string;
  groupName?: string;
};

export type OptionWithFlag = Option & {
  isAiPrediction?: boolean;
};

export type ShuGroupList = {
  name: string;
  id: string;
  shuList: string[];
};

export type Area = {
  id: string;
  name: string;
  halls: string[];
};

export type HallsOfMarketArea = {
  code: string;
  name: string;
};

export type Hall = {
  code: string;
  name: string;
  hallsOfMarketArea?: {
    code: string;
    name: string;
  }[];
};

export type HallWithFlag = Hall & {
  isFT: boolean;
};

export type GraphColumn = {
  name: string;
  type: string;
  digitsAfterDecimalPoint?: number | null;
};

export type GraphData = {
  columns: GraphColumn[];
  rows: (string | number)[][];
};

export type SummaryData<T = SummaryDataElement, U = SummaryDataElement> = {
  name?: string;
  original: T;
  comparison?: U | undefined;
};

export type SummaryDataElement = {
  value: string;
  unit: string;
  color: string;
};

export type SummaryDataElementWithRank = {
  rank: '1' | '2' | '3' | null;
} & SummaryDataElement;

export const hideColumns = (
  columns: Column[],
  rows: Row[],
  hiddenColumnCodes: string[]
) => {
  if (hiddenColumnCodes.length === 0) {
    return {
      columns,
      rows,
    };
  }

  const filteredRows = rows.map((row) => {
    if (columns.length !== row.data.length) {
      throw Error('カラムの数と行データの数が一致しません');
    }

    return {
      ...row,
      data: row.data.filter((_, i) => {
        const column = columns.at(i);
        return column ? !hiddenColumnCodes.includes(column.code) : true;
      }),
    };
  });
  const filteredColumns = columns.filter(
    ({ code }) => !hiddenColumnCodes.includes(code)
  );

  return { columns: filteredColumns, rows: filteredRows };
};

export type KiWithMakerListOptions = {
  code: string;
  name: string;
  maker: string;
};

/**
 * メーカーグループの型
 */
export type MakerGroup = {
  groupCd: string;
  groupName: string;
};

export type LoadingState = 'prepare' | 'loading' | 'loaded' | 'failed';
